import React from 'react'
import Loader from 'components/_UI/Loader'
import styled, { css } from 'styled-components'
import colors from 'theme/colors'
import breakpoints from 'theme/breakpoints'
import date from 'helpers/date'
import { Clock, MapPin, FileText, Inbox } from 'lucide-react'

export default function OrdersList({ data, expired, home, renderDetail, isLoading, searchTerm }) {

    const filtered = data?.filter((el) => {
        if (!searchTerm) return true
        const term = searchTerm.toLowerCase()
        return el.name?.toLowerCase().includes(term)
    })

    if (isLoading) {
        return (
            <LoaderWrapper>
                <Loader />
            </LoaderWrapper>
        )
    }

    if (!filtered?.length) {
        return (
            <EmptyState>
                <EmptyIconCircle>
                    <Inbox size={32} color={colors.occasionalPurple} />
                </EmptyIconCircle>
                <EmptyTitle>
                    {searchTerm
                        ? 'Nenhum resultado encontrado'
                        : 'Não há ingressos para próximos eventos'
                    }
                </EmptyTitle>
                <EmptySubtitle>
                    {searchTerm
                        ? 'Tente buscar com outros termos.'
                        : 'Quando você adquirir ingressos, eles aparecerão aqui.'
                    }
                </EmptySubtitle>
                {!searchTerm && (
                    <EmptyCta onClick={() => window.location.href = '/'}>
                        Encontrar eventos
                    </EmptyCta>
                )}
            </EmptyState>
        )
    }

    return (
        <CardGrid>
            {filtered.map((el, index) => (
                <WalletCard
                    key={index}
                    onClick={() => renderDetail(el)}
                    expired={expired}
                >
                    <CardImageWrapper>
                        <CardImage
                            src={
                                el.path
                                    ? el.path
                                    : el?.images?.length && el.images[0]
                                        ? el.images[0]
                                        : '/assets/images/placeholder-events.png'
                            }
                            alt={el.name}
                        />
                        {expired && (
                            <ExpiredBadge>Encerrado</ExpiredBadge>
                        )}
                    </CardImageWrapper>

                    <CardBody>
                        <CardTitle>{el.name}</CardTitle>

                        {el?.dates && el.dates[0] && (
                            <CardInfoRow>
                                <Clock size={14} color={colors.night} />
                                <CardInfoText>
                                    {date.format(el.dates[0].date)} &bull; {el.dates[0].start?.substring(0, 5)}
                                </CardInfoText>
                            </CardInfoRow>
                        )}

                        <CardInfoRow>
                            <MapPin size={14} color={colors.night} />
                            <CardInfoText>
                                {!el?.address
                                    ? 'Local a definir'
                                    : el.address?.raw_address
                                        ? el.address.raw_address
                                        : `${el.address?.street?.name || ''}, ${el.address?.street?.district || ''}`
                                }
                            </CardInfoText>
                        </CardInfoRow>

                        <CardDivider />

                        <CardAction>
                            <FileText size={14} color={colors.occasionalPurple} />
                            <CardActionText>Ver pedido</CardActionText>
                        </CardAction>
                    </CardBody>
                </WalletCard>
            ))}
        </CardGrid>
    )
}

const LoaderWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding: 60px 0;
`;

const CardGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    padding-top: 24px;

    @media screen and (min-width: ${breakpoints.sm}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (min-width: ${breakpoints.lg}) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const WalletCard = styled.div`
    background: ${colors.neutralLight};
    border-radius: 12px;
    border: 1px solid ${colors.neutralDark};
    overflow: hidden;
    cursor: pointer;
    transition: all .25s ease;

    ${({ expired }) => expired && css`
        opacity: 0.7;
    `}

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
    }
`;

const CardImageWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 160px;

    @media screen and (min-width: ${breakpoints.md}) {
        height: 180px;
    }
`;

const CardImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const ExpiredBadge = styled.div`
    position: absolute;
    bottom: 10px;
    right: 10px;
    background: ${colors.danger};
    color: ${colors.neutralLight};
    font-size: 11px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 20px;
    letter-spacing: 0.02em;
`;

const CardBody = styled.div`
    padding: 16px;
`;

const CardTitle = styled.h3`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 12px 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
`;

const CardInfoRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 6px;

    & > svg {
        flex-shrink: 0;
        margin-top: 2px;
    }
`;

const CardInfoText = styled.span`
    font-size: 13px;
    color: ${colors.night};
    line-height: 1.4;
`;

const CardDivider = styled.div`
    width: 100%;
    height: 1px;
    background: ${colors.neutralDark};
    margin: 14px 0 12px 0;
`;

const CardAction = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CardActionText = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.occasionalPurple};
    text-transform: uppercase;
    letter-spacing: 0.02em;
`;

/* ---- Empty State ---- */

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 24px;
    text-align: center;
`;

const EmptyIconCircle = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: #F3EAFF;
    margin-bottom: 20px;
`;

const EmptyTitle = styled.p`
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0 0 6px 0;
`;

const EmptySubtitle = styled.p`
    font-size: 14px;
    color: ${colors.night};
    margin: 0 0 24px 0;
    max-width: 360px;
`;

const EmptyCta = styled.button`
    background: ${colors.occasionalPurple};
    color: ${colors.neutralLight};
    border: none;
    border-radius: 8px;
    padding: 12px 28px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all .2s ease;
    text-transform: uppercase;
    letter-spacing: 0.03em;

    &:hover {
        opacity: 0.9;
        box-shadow: 0 4px 12px ${colors.occasionalPurple}40;
    }

    &:active {
        transform: scale(0.98);
    }
`;
